<script lang="ts">
  import { intersection } from "@opera-gx/common/src/actions/intersection";
  import { onDestroy, onMount } from "svelte";

  import Image from "../Image.svelte";
  import Tile from "../Tile/Tile.svelte";
  import Badge from "./Badge.svelte";

  export let lazy = true;
  export let tags = "";
  export let title = "";
  export let description = "";
  export let imageUrl: string;
  export let badge;
  // TODO(duanei): We should check if we could prefetch the url just before we navigate to the
  // game so there is less time spent on a blank screen.
  export let href: string;
  // TODO(duanei): Get playText from CMS maybe?
  const playText = "Play";

  let intersecting = false;
  let loaded = false;

  const handleLoad = () => {
    loaded = true;
  };

  let showPlaceholder = false;
  let timeout: NodeJS.Timeout;
  onMount(() => {
    timeout = setTimeout(() => {
      showPlaceholder = true;
    }, 1000);
  });
  onDestroy(() => {
    clearTimeout(timeout);
  });
</script>

<style lang="postcss">
  .fader {
    opacity: 0;
    transition: opacity 0.5s ease-out;
  }

  .visible {
    opacity: 1;
  }

  .wrapper {
    user-select: none;
    color: white;
    transition:
      opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
      transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    opacity: 0;
    transform: translateX(-15px);
    will-change: opacity, transform;

    &.show {
      opacity: 1;
      transform: translateX(0);
    }

    &:active {
      transform: scale(1.04);
    }
  }

  .image {
    position: absolute;
    inset: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .inner {
    box-sizing: border-box;
    position: relative;
    aspect-ratio: 16/9;
    display: flex;
    flex-direction: column;
    padding: 19px 15px;
    justify-content: flex-end;
  }

  .tags {
    color: theme(colors.accent.DEFAULT);
    font-weight: 590;
    font-size: 14px;
    line-height: 12px;
    white-space: nowrap;
  }

  .title {
    color: white;
    text-shadow:
      0 -1.5px 2px rgb(0 0 0 / 40%),
      0 1.5px 2px rgb(0 0 0 / 40%),
      1.5px 0 2px rgb(0 0 0 / 40%),
      -1.5px 0 2px rgb(0 0 0 / 40%);
    font-weight: 590;
    font-size: 20px;
    line-height: 24px;
    font-style: italic;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: -4px;
    margin-bottom: 0;
    padding: 4px;
  }

  .description {
    text-shadow:
      0 -1.5px 2px rgb(0 0 0 / 40%),
      0 1.5px 2px rgb(0 0 0 / 40%),
      1.5px 0 2px rgb(0 0 0 / 40%),
      -1.5px 0 2px rgb(0 0 0 / 40%);
    overflow: hidden;
    font-weight: 510;
    font-size: 14px;
    line-height: 18px;
    margin: -4px;
    margin-bottom: 2px;
    padding: 4px;
  }

  .play {
    margin-top: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 24px;
    width: 60px;
    background-color: theme(colors.accent.DEFAULT);
    font-weight: 590;
    font-size: 11px;
    border-radius: 2.6111px;
  }

  .badge {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
  }
</style>

<a {href} on:click>
  <div
    class="wrapper"
    class:show={intersecting && (loaded || showPlaceholder)}
    use:intersection={{
      handler: (entries) => {
        if (!entries.some(({ isIntersecting }) => isIntersecting)) {
          intersecting = false;
          return;
        }

        if (entries.some(({ intersectionRatio }) => intersectionRatio >= 0.05)) {
          intersecting = true;
          return;
        }
      },
      rootMargin: "100% 0px 0px 0px",
      threshold: [0, 0.05],
    }}>
    <Tile cut={40} let:class={tile}>
      <div
        class="{tile} to-accent/40 after:to-accent bg-gradient-to-br from-transparent after:bg-gradient-to-br after:from-transparent after:via-transparent">
        {#if badge}
          <div class="badge">
            <Badge color={badge.color}>{badge.name}</Badge>
          </div>
        {/if}
        <div class="image">
          <Image src={imageUrl} w={686} h={386} let:srcset let:at1x alt="">
            <img
              loading={lazy ? "lazy" : "eager"}
              {srcset}
              src={at1x}
              alt=""
              class="fader"
              class:visible={loaded}
              on:load|once={handleLoad} />
          </Image>
        </div>
        <div class="inner">
          <div class="tags">{tags}</div>
          <div class="title">{title}</div>
          {#if description}
            <div class="description">{description}</div>
          {/if}
          <div class="play">{playText}</div>
        </div>
      </div>
    </Tile>
  </div>
</a>
